// ────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// Extracted from DataProvider for reusability
// ────────────────────────────────────────────────────────────────────

export const todaySeed = () => new Date().getDate();

export const seededRandom = (seed) => {
  const s = Number.isFinite(seed) ? seed : 0;
  const x = Math.sin(s) * 10000;
  return x - Math.floor(x);
};

export const clampInt = (n, min, max) => {
  const v = Math.floor(Number(n));
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
};

export const shuffleWithSeed = (arr, limit = 100) => {
  try {
    const list = Array.isArray(arr) ? arr.slice() : [];
    if (list.length < 2) return list.slice(0, clampInt(limit, 0, list.length));
    const seedBase = clampInt(todaySeed(), 0, 366);
    const safeLimit = clampInt(limit, 0, list.length);
    for (let i = list.length - 1; i > 0; i--) {
      const r = seededRandom(seedBase + i);
      const j = clampInt(Math.floor(r * (i + 1)), 0, i);
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list.slice(0, safeLimit);
  } catch {
    const safe = Array.isArray(arr) ? arr.slice() : [];
    return safe.slice(0, clampInt(limit, 0, safe.length));
  }
};

export const normalizePositiveInt = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};

export const toLower = (value) => {
  if (!value) return '';
  return typeof value === 'string'
    ? value.toLowerCase()
    : (typeof value === 'object' && value && 'text' in value)
    ? String(value.text).toLowerCase()
    : '';
};

export const parseIdList = (v) => {
  if (Array.isArray(v)) {
    return v
      .map(n => Number(n))
      .filter(n => Number.isFinite(n));
  }
  if (typeof v === 'string') {
    return v.split(',')
      .map(s => Number(String(s).trim()))
      .filter(n => Number.isFinite(n));
  }
  return [];
};

export const dedupePreserveOrder = (arr) => {
  const seen = new Set();
  const out = [];
  for (const n of arr) {
    if (!seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
};

export const fallbackText = (...vals) =>
  vals.find(v => typeof v === 'string' && v.trim().length > 0)?.trim() || '';

// ────────────────────────────────────────────────────────────────────
// SCORE BOOST (legacy keyword params)
// ────────────────────────────────────────────────────────────────────
const qs = new URLSearchParams(window.location.search);

const legacyKeywordList = (prefix) => {
  const out = [];
  for (let i = 1; ; i++) {
    const v = qs.get(`${prefix}_${i}`);
    if (!v) break;
    out.push(v);
  }
  return out;
};

const PRIMARY_KEYWORDS = legacyKeywordList('keyword_primary');
const SECONDARY_KEYWORDS = legacyKeywordList('keyword_secondary');

const makeRegex = (arr) =>
  (arr.length ? new RegExp(arr.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'i') : null);

const primaryMatch = makeRegex(PRIMARY_KEYWORDS);
const secondaryMatch = makeRegex(SECONDARY_KEYWORDS);

export const boostScore = ({ description, featured_title }) => {
  const desc = toLower(description);
  const title = toLower(featured_title);
  return (
    (primaryMatch && (primaryMatch.test(desc) || primaryMatch.test(title)) ? 1 : 0) +
    (secondaryMatch && (secondaryMatch.test(desc) || secondaryMatch.test(title)) ? 0.5 : 0)
  );
};

// ────────────────────────────────────────────────────────────────────
// EXPERT NORMALIZATION
// ────────────────────────────────────────────────────────────────────
export const normalizeExpert = (e, { isVip = false } = {}) => {
  const description = fallbackText(
    e.description,
    e.headline,
    e.featured_title_at_company,
    e.featured_title
  );
  const withDesc = { ...e, description };

  if (Number(e.id) === 155776) withDesc.slug = 'suze-orman';

  return {
    ...withDesc,
    isVip,
    sortWeight: boostScore(withDesc),
  };
};

// ────────────────────────────────────────────────────────────────────
// SIMILARITY API
// ────────────────────────────────────────────────────────────────────
export const buildSimilarityUrl = (query) => {
  const limitFromQs = normalizePositiveInt(qs.get('sim_limit'), 300);
  const qsObj = new URLSearchParams({
    query,
    search_model_types: 'Id::PromoEntity::Expert',
    embedding_types: 'MatchFeed::AggregatedEmbedding',
    limit: String(limitFromQs),
  });
  return `https://app2.qwoted.com/match_feed/api/similarity_search?${qsObj}`;
};
